import { FormLeavingListener } from '@exam-center/components';
import {
  GetExamRoomRequestInfoByIdDocument,
  useCreateOrUpdateExamRoomRequestComputerInfoMutation,
  useGetDownloadSignedUrlForExamRoomRequestComputerInfoLazyQuery,
  useGetUploadSignedUrlForExamRoomRequestComputerInfoLazyQuery,
} from '@exam-center/graphql';
import { Box, Button, Divider, Flex, Form, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { ComputerInfoFeatureCheckRow, PhotoSetRow } from '../../components';
import { useExamRoomRequestInfo } from '../../providers';

import {
  COMPUTER_DETAILS_FEATURES,
  INTERNET_DETAILS_FEATURES,
  SECURITY_DETAILS_FEATURES,
} from './constants';
import {
  ExamLocationRequestRoomComputerInfoFormSchema,
  type ExamLocationRequestRoomComputerInfoFormInputs,
} from './schema';
import { type DeepPartial } from './utils';

interface ExamLocationRequestRoomComputerInfoFormProps {
  isReadonly?: boolean;
  hideSubmit?: boolean;
  submitLabel?: string;
  defaultValues?: DeepPartial<ExamLocationRequestRoomComputerInfoFormInputs>;
}

export default function ExamLocationRequestRoomComputerInfoForm(
  props: ExamLocationRequestRoomComputerInfoFormProps,
) {
  const { isReadonly, hideSubmit, submitLabel = 'บันทึก', defaultValues } = props;

  const { showSnackbar } = useSnackbar();

  const { examRoomRequestInfo } = useExamRoomRequestInfo();

  const [searchParams] = useSearchParams();
  const shouldValidate = searchParams.get('validate') === 'true';

  const [createOrUpdateExamRequestRoomRequestComputerInfo, { loading }] =
    useCreateOrUpdateExamRoomRequestComputerInfoMutation();

  const form = useForm<ExamLocationRequestRoomComputerInfoFormInputs>({
    defaultValues: defaultValues,
    resolver: shouldValidate
      ? zodResolver(ExamLocationRequestRoomComputerInfoFormSchema)
      : undefined,
  });

  const [getUploadSignedUrls] = useGetUploadSignedUrlForExamRoomRequestComputerInfoLazyQuery({
    fetchPolicy: 'no-cache',
  });
  const [getDownloadSignedUrls] = useGetDownloadSignedUrlForExamRoomRequestComputerInfoLazyQuery({
    fetchPolicy: 'no-cache',
  });

  useEffect(() => {
    if (shouldValidate) {
      form.trigger();
    }
  }, [shouldValidate, form]);

  useEffect(() => {
    const frameContent = document.getElementById('frame-content');
    if (frameContent) {
      frameContent.scrollTo({ top: 0, behavior: 'auto' });
    }
  }, []);

  function uploadFiles(files: File[]): Promise<string[]> {
    const run = async () => {
      const uploadResult = await getUploadSignedUrls({
        variables: {
          getUploadSignedUrlForExamRoomRequestComputerInfoInput: {
            contentTypes: files.map((f) => f.type),
            examRoomRequestInfoId: examRoomRequestInfo.id,
          },
        },
      });

      const signedUploadUrls =
        uploadResult.data?.ecGetUploadSignedUrlForExamRoomRequestComputerInfo.signedUrls ?? [];

      if (signedUploadUrls.length < files.length) {
        throw new Error('Not enough signed URLs returned for upload');
      }

      await Promise.all(
        files.map((file, idx) => {
          const signed = signedUploadUrls[idx];
          if (!signed?.url) {
            throw new Error('No matching signed URL found');
          }
          return fetch(signed.url, {
            method: 'PUT',
            headers: { 'Content-Type': file.type },
            body: file,
          });
        }),
      );

      const fileNames = signedUploadUrls.map((url) => url.storageObject.fileName ?? '');

      return fileNames;
    };

    return run();
  }

  function getImagesDownloadSignedUrl(fileNames: string[]): Promise<string[]> {
    const run = async () => {
      const downloadResult = await getDownloadSignedUrls({
        variables: {
          getDownloadSignedUrlForExamRoomRequestComputerInfoInput: {
            fileNames,
            examRoomRequestInfoId: examRoomRequestInfo.id,
          },
        },
      });

      return (
        fileNames?.map((fileName) => {
          return downloadResult.data?.ecGetDownloadSignedUrlForExamRoomRequestComputerInfo
            .signedUrls[fileName ?? ''];
        }) ?? []
      );
    };

    return run();
  }

  const onSubmit = (data: ExamLocationRequestRoomComputerInfoFormInputs) => {
    createOrUpdateExamRequestRoomRequestComputerInfo({
      variables: {
        createOrUpdateExamRoomRequestComputerInfoInput: {
          examRoomRequestInfoId: examRoomRequestInfo.id,
          info: {
            ...data,
            securityDetails: {
              ...data.securityDetails,
              isSyncedWithThaiServer: data.securityDetails.isSyncedWithThaiServer === 'synced',
            },
          },
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'บันทึกข้อมูลคอมพิวเตอร์เรียบร้อยแล้ว',
          variant: 'success',
        });
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการบันทึกข้อมูลคอมพิวเตอร์',
          variant: 'error',
        });
      },
      refetchQueries: [GetExamRoomRequestInfoByIdDocument],
    });
  };

  return (
    <FormProvider {...form}>
      <Form
        onSubmit={form.handleSubmit(onSubmit)}
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing(8),
          padding: theme.spacing(8),
        })}
      >
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(6),
            [theme.breakpoints.down('desktop')]: {
              flexDirection: 'column',
            },
          })}
        >
          <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
            ข้อมูลเกี่ยวกับอุปกรณ์คอมพิวเตอร์
          </Text>
          <Box
            sx={(theme) => ({
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(6),
            })}
          >
            {COMPUTER_DETAILS_FEATURES.map((f) => (
              <ComputerInfoFeatureCheckRow key={f.key} config={f} isReadonly={isReadonly} />
            ))}
            <PhotoSetRow
              name='computerDetails.computerSpecImages'
              label='9.ภาพสเปคคอมพิวเตอร์'
              imageInputLabel='แนบรูปภาพสเปคคอมพิวเตอร์'
              isReadonly={isReadonly}
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
            />
            <PhotoSetRow
              name='computerDetails.computerSeatImages'
              label='10.ภาพคอมพิวเตอร์ และที่นั่งสอบ'
              imageInputLabel='แนบรูปภาพบริเวณคอมพิวเตอร์ และที่นั่งสอบ'
              isReadonly={isReadonly}
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
            />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(6),
            [theme.breakpoints.down('desktop')]: {
              flexDirection: 'column',
            },
          })}
        >
          <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
            ข้อมูลเกี่ยวกับอินเตอร์เน็ต
          </Text>
          <Box
            sx={(theme) => ({
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(6),
            })}
          >
            {INTERNET_DETAILS_FEATURES.map((f) => (
              <ComputerInfoFeatureCheckRow key={f.key} config={f} isReadonly={isReadonly} />
            ))}
            <PhotoSetRow
              name='internetDetails.speedTestImages'
              label='2.ภาพการทดสอบความเร็วอินเทอร์เน็ตสามารถทดสอบได้ที่ (speedtest.net หรือ fast.com)'
              imageInputLabel='แนบรูปภาพความเร็วอินเตอร์เน็ต'
              isReadonly={isReadonly}
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
            />
          </Box>
        </Box>
        <Divider sx={{ my: 2 }} />
        <Box
          sx={(theme) => ({
            display: 'flex',
            flexDirection: 'row',
            gap: theme.spacing(6),
            [theme.breakpoints.down('desktop')]: {
              flexDirection: 'column',
            },
          })}
        >
          <Text variant='body1Semibold' sx={(theme) => ({ width: theme.spacing(60) })}>
            ข้อมูลเกี่ยวกับความปลอดภัย
          </Text>
          <Box
            sx={(theme) => ({
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: theme.spacing(6),
            })}
          >
            {SECURITY_DETAILS_FEATURES.slice(0, 3).map((f) => (
              <ComputerInfoFeatureCheckRow key={f.key} config={f} isReadonly={isReadonly} />
            ))}
            <PhotoSetRow
              name='securityDetails.timeSyncImages'
              label='4.ภาพเวลาเครื่องในระบบ'
              imageInputLabel='แนบรูปภาพเวลาเครื่องในระบบ'
              isReadonly={isReadonly}
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
            />
            {SECURITY_DETAILS_FEATURES.slice(3).map((f) => (
              <ComputerInfoFeatureCheckRow key={f.key} config={f} isReadonly={isReadonly} />
            ))}
            <PhotoSetRow
              name='securityDetails.robotTestImages'
              label='7.ภาพการทดสอบ Robot Test'
              imageInputLabel='แนบรูปภาพการทดสอบ Robot'
              isReadonly={isReadonly}
              uploadFiles={uploadFiles}
              getFilesDownloadSignedUrls={getImagesDownloadSignedUrl}
            />
          </Box>
        </Box>
        {!hideSubmit ? (
          <Flex
            sx={(theme) => ({
              justifyContent: 'flex-end',
              mt: theme.spacing(2),
              gap: theme.spacing(2),
            })}
          >
            <Button
              type='submit'
              disabled={isReadonly || form.formState.isSubmitting || !form.formState.isDirty}
              loading={form.formState.isSubmitting || loading}
            >
              {submitLabel}
            </Button>
          </Flex>
        ) : null}
      </Form>
      {isReadonly ? null : <FormLeavingListener onSubmit={onSubmit} />}
    </FormProvider>
  );
}
